import React, { useCallback, useEffect, useState } from 'react';

import { type EmojiMatcher } from '@tari-labs/emojis-wasm-admin';
import { useTranslation } from 'react-i18next';

import {
    Button,
    Code,
    Dropdown,
    EmojiPicker,
    LabelledInput,
    StyledToggleHeading,
    Typography,
    Wrapper,
} from '@pkg/components';
import ValidTextInput from '@pkg/components/Elements/ValidTextInput';
import { StyledForm, StyledLabel } from '@pkg/components/Sections/Admin/components/Form/styles';
import { useAppDispatch, useEmojiWasm } from '@pkg/hooks';
import { useCheckEmoji, useMutateEmojiGroup } from '@pkg/hooks/admin/emoji';
import { incrementDate } from '@pkg/utils';

import { type EmojiGroupCheckResponse, type EmojiGroupsAction, type Pattern } from '@app/client';
import { prefetch2FA, wasmPatternCheck } from '@app/helpers';
import { addNotification } from '@app/store/actions/notification';
import { type EmojiAdminWasm, WasmType } from '@app/store/types/emoji';
import { type NotificationColor } from '@app/store/types/notification';

export function EditPatterns({ onServerMatch }: { onServerMatch?: (match?: EmojiGroupCheckResponse) => void }) {
    const { t } = useTranslation(['common']);
    const { emojiWasm: adminWasm } = useEmojiWasm<EmojiAdminWasm>(WasmType.EmojiAdminWasm);
    const { createGroup } = useMutateEmojiGroup();

    const [pattern, setPattern] = useState('' as Pattern);

    const [tagString, setTagString] = useState('');
    const [tags, setTags] = useState([] as string[]);
    const [action, setAction] = useState('Reserve');

    const [showHelp, setShowHelp] = useState(false);

    const [matcher, setMatcher] = useState<EmojiMatcher>();

    const [testYat, setTestYat] = useState('');
    const [rank, setRank] = useState<number>(0);
    const { data: checkEmojiData } = useCheckEmoji(testYat);

    useEffect(() => {
        if (checkEmojiData) {
            onServerMatch?.(checkEmojiData);
        } else {
            onServerMatch?.(undefined);
        }
    }, [checkEmojiData, onServerMatch]);

    const tenYearArray = incrementDate(10).split(':');
    tenYearArray.pop();

    const tenYears = tenYearArray.join(':');
    const [onSaleFrom, setOnSaleFrom] = useState(tenYears);
    const dispatch = useAppDispatch();

    const showNotification = useCallback(
        (message: string, severity = 'error' as NotificationColor) => {
            dispatch(
                addNotification({
                    message,
                    severity,
                }),
            );
        },
        [dispatch],
    );

    const handleRankChange = (rank: string) => {
        setRank(+rank);
    };

    const getAction = useCallback(() => {
        //"2033-11-29T11:02:54.238Z:00+00:00"
        //onsale_from: "2023-12-07T13:06:00+00:00
        return action === 'Sell' ? 'Sell' : { Reserve: { onsale_from: onSaleFrom + ':00+00:00' } };
    }, [action, onSaleFrom]);

    const save = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();
            const action = getAction() as EmojiGroupsAction;
            const value = { pattern, tags, action, rank };
            createGroup({ prefetch: prefetch2FA(dispatch), value })
                .then((r) => {
                    if (r?.ok) {
                        showNotification('Added your pattern', 'success');
                    } else {
                        showNotification('Error adding your pattern, please check it and try again.');
                    }
                })
                .catch(() => {
                    showNotification('Error adding your pattern, please check it and try again.');
                });
        },
        [getAction, pattern, tags, rank, createGroup, dispatch, showNotification],
    );

    function hasErrorInPatternMatch(eid: string) {
        if (matcher && eid) {
            try {
                return !matcher?.matches(eid);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (e: any) {
                return e.toString();
            }
        }
        return true;
    }

    function hasErrorInPattern(pattern: string): string | boolean {
        if (!adminWasm) return true;

        const { matcher, response } = wasmPatternCheck(adminWasm, pattern);
        setMatcher(matcher);

        return response;
    }

    function parseTagString(tagString: string) {
        setTagString(tagString);
        if (tagString[tagString.length - 1] === ',') {
            const tag = tagString.slice(0, -2);
            addTag(tag);
        }
    }

    function onTagKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        e.stopPropagation();
        if (e.key === 'Enter') {
            addTag(tagString);
        }
    }

    function tagBlur() {
        addTag(tagString);
    }

    function addTag(tag: string) {
        const trimmedTag = tag.trim();
        if (trimmedTag === '') {
            return;
        }

        if (tags.indexOf(trimmedTag) !== -1) {
            return;
        }
        setTagString('');
        tags.push(tag);
        setTags(tags);
    }

    function removeTag(tag: string) {
        tags.splice(tags.indexOf(tag), 1);
        setTags([...tags]);
    }

    function toggleHelp() {
        setShowHelp(!showHelp);
    }

    const handleEmojiSelect = (selected = '') => {
        setTestYat(`${testYat}${selected}`);
    };

    const instructionMarkup = showHelp ? (
        <Wrapper>
            <p>
                <strong>A pattern matcher for emoji strings</strong>
                <br />
                The matcher is a string that is similar to a regular expression, but made simpler for emoji.id admins to
                use.
                <br />
                <br />
                Given a string,
                <br />- Every emoji from the character set is treated as a literal.
                <br />- Every lowercase letter (a-f) matches any emoji, but different letters can match the same emoji.
                <br />- A letter or literal followed by <Code>?</Code> is optional - If the last character is{' '}
                <Code>!</Code>, then the emoji matched by the letters above must be distinct
                <br />- Any other character is invalid
                <br />
                <br />
                <strong>Examples:</strong>
                <br />- <Code>&ldquo;aabbcc&rdquo;</Code> would match all 6-char emojis with 3 repeated double emoji
                (not necessarily distinct)
                <br />- <Code>&ldquo;aabbcc!&rdquo;</Code> would match all 6-char emojis with 3 repeated <i>distinct</i>
                double emoji
                <br />-{' '}
                <Code>
                    &ldquo;
                    <span role="img" aria-label={t('monkey_emojis')}>
                        🙈🙉🙊
                    </span>
                    &rdquo;
                </Code>{' '}
                would match that single eid
                <br />-
                <Code>
                    &ldquo;
                    <span role="img" aria-label={t('basket_ball')}>
                        🏀
                    </span>
                    abc?d?e?!&rdquo;
                </Code>{' '}
                would reserve all 3-6 char eids starting with a basketball and 2-5 distinct other emoji
            </p>
        </Wrapper>
    ) : null;

    const tagMarkup = (
        <Wrapper width="50%">
            <LabelledInput
                placeholder="tags"
                onChange={(e) => {
                    parseTagString(e.target.value);
                }}
                onBlur={tagBlur}
                onKeyDown={onTagKeyDown}
                value={tagString}
                name="tags"
                id="tags"
                label="Tags"
                type="text"
            />
            {tags?.map((g) => (
                <li
                    className="remove"
                    onClick={() => {
                        removeTag(g);
                    }}
                    key={g}
                >
                    {g}
                </li>
            ))}
        </Wrapper>
    );

    const actionMarkup = (
        <Wrapper width="50%">
            <StyledLabel>Action</StyledLabel>
            <Dropdown
                onChange={(e) => {
                    setAction(e.target.value);
                }}
                value={action}
            >
                <option value="Reserve">Reserve</option>
                <option value="Sell">Sell</option>
            </Dropdown>
            {action === 'Reserve' && (
                <LabelledInput
                    name="reserved"
                    label="Reserved until"
                    type="datetime-local"
                    value={onSaleFrom}
                    onChange={(e) => {
                        setOnSaleFrom(e.target.value);
                    }}
                />
            )}
        </Wrapper>
    );

    const patternCheckerMarkup = (
        <Wrapper width="50%">
            <StyledLabel>Pattern</StyledLabel>
            <ValidTextInput
                placeholder="Pattern"
                value={pattern}
                onChange={(e) => {
                    setPattern(e.target.value);
                    setTestYat(testYat + ' ');
                    setTimeout(() => {
                        setTestYat(testYat.trim());
                    }, 1);
                }}
                hasErrorCheck={hasErrorInPattern}
            />
            <ValidTextInput
                placeholder="Test Emoji"
                value={testYat}
                dirty={true}
                onChange={(e) => {
                    setTestYat(e.target.value);
                }}
                hasErrorCheck={hasErrorInPatternMatch}
            />
            {checkEmojiData ? (
                <>
                    <hr />
                    <strong>🔔 Match found! Check the table below</strong>
                    <hr />
                </>
            ) : null}
            <div className="admin-emoji-picker-wrapper">
                <EmojiPicker onSelectEmoji={handleEmojiSelect} darkMode />
            </div>
        </Wrapper>
    );
    return (
        <Wrapper>
            <br />
            <StyledToggleHeading type="button" onClick={toggleHelp}>
                <Wrapper direction="row" justifyContent="space-between">
                    <Typography type="headline" size={20}>
                        {`${showHelp ? 'Collapse' : 'Expand'} Instructions`}
                    </Typography>
                    <img
                        onClick={toggleHelp}
                        className={`more-info ${showHelp ? 'open' : ''}`}
                        src="/assets/icons/info-icon.svg"
                        alt="More Info"
                    />
                </Wrapper>
            </StyledToggleHeading>
            {instructionMarkup}
            <StyledForm>
                <Wrapper direction="row" justifyContent="space-between" gap="20px" alignItems="flex-start">
                    {tagMarkup}
                    <Wrapper width="50%">
                        <LabelledInput
                            label="Rank"
                            name="rank"
                            type="number"
                            value={rank}
                            min={1}
                            onChange={(e) => {
                                handleRankChange(e.target.value);
                            }}
                        />
                    </Wrapper>
                </Wrapper>
                <hr />
                <Wrapper direction="row" justifyContent="space-between" gap="20px" alignItems="flex-start">
                    {actionMarkup}
                    {patternCheckerMarkup}
                </Wrapper>
                <hr />
                <Button type="button" onClick={(e) => save(e)} color="secondary" size="small" disabled={!pattern}>
                    Save Group Settings
                </Button>
            </StyledForm>
        </Wrapper>
    );
}
